import React, { Component } from "react";
import { Maybe } from "true-myth";
import { any, toPairs, reduce, values } from "ramda";

import { SchedulingActionTypes, MODAL_STATUS } from "actions/scheduling";
import { trackEvent } from "helpers/analytics";
import PermalinkContext from "helpers/context/permalink";
import RenderForFlag from "helpers/launch_darkly";
import { NotifyType } from "helpers/schedule/scheduling_page";

import ActionBar from "components/schedule/action_bar";
import LaborDrawer from "containers/labor/labor_drawer_container";
import NotifyModal from "components/schedule/notify_modal";
import ScheduleTable from "components/schedule/schedule_table";
import StyledLoaderOverlay from "components/shared/styled_loader_overlay";

import { IScheduleFilters } from "types/reducers";
import { RequestStatus } from "types/requests";
import { ISchedule, IPeople } from "types/schedules";

export interface ISchedulingPage {
  dayView: Maybe<Date>;
  filters: IScheduleFilters;
  interactiveView: boolean;
  locationsRequestStatus: RequestStatus;
  orderedSchedule: ISchedule;
  permalink: Maybe<string>;
  publicURL: string;
  publishedSchedule: Maybe<ISchedule>;
  rawSchedule: ISchedule;
  requestStatuses: {
    draftScheduleGet: RequestStatus;
    notifySchedulePost: RequestStatus;
    posUsersGet: RequestStatus;
    publishedScheduleGet: RequestStatus;
    publishSchedulePost: RequestStatus;
    unavailGet: RequestStatus;
  };
  showNotifyScheduleModal: MODAL_STATUS;
  startDate: Maybe<Date>;
  endDate: Maybe<Date>;
  storePrettyUrl: string;
  autoSaveDraft: () => void;
  cancelAutoSaveDraft: () => void;
  clearFilters: () => void;
  fetchDayParts: (storePrettyUrl: string) => void;
  fetchDraftSchedule: (
    startDate: Date,
    endDate: Date,
    location: string
  ) => void;
  fetchPosUsers: (location: string, prettyUrl: string) => void;
  fetchPublishedSchedule: (
    startDate: Date,
    endDate: Date,
    location: string,
    forPublic: boolean
  ) => void;
  fetchSalesForecast: (storePrettyUrl: string) => void;
  fetchTemplates: (location: string) => void;
  fetchUnavail: (location: string) => void;
  publishSchedule: () => void;
  notifyPosUsers: (
    location: string,
    startDate: Date,
    endDate: Date,
    people: IPeople,
    notifyType: NotifyType
  ) => void;
  setScheduleDateBoundaries: (
    startDate: Date,
    endDate: Date
  ) => SchedulingActionTypes;
  toggleNotifyModal: (status: MODAL_STATUS) => void;
}

class SchedulingPage extends Component<ISchedulingPage> {
  public componentDidMount() {
    const {
      fetchDraftSchedule,
      fetchPublishedSchedule,
      fetchTemplates,
      fetchUnavail,
      autoSaveDraft,
      interactiveView,
      permalink,
      setScheduleDateBoundaries,
      startDate,
      endDate,
    } = this.props;

    permalink.map((location: string) => {
      fetchTemplates(location);

      Maybe.all(startDate, endDate).map(([start, end]) => {
        setScheduleDateBoundaries(start, end);
        fetchDraftSchedule(start, end, location);
        fetchPublishedSchedule(start, end, location, !interactiveView);
        fetchUnavail(location);
        autoSaveDraft();
        return start;
      });

      return location;
    });
  }

  public componentDidUpdate() {
    const {
      fetchDayParts,
      fetchPosUsers,
      fetchSalesForecast,
      locationsRequestStatus,
      permalink,
      requestStatuses,
      storePrettyUrl,
    } = this.props;

    if (
      locationsRequestStatus === RequestStatus.SUCCESS &&
      requestStatuses.posUsersGet === RequestStatus.NOT_STARTED
    ) {
      // Run fetch actions which depend on permalink/storyPrettUrl
      permalink.match({
        Just: (bcLink) => {
          fetchSalesForecast(storePrettyUrl);
          fetchDayParts(storePrettyUrl);
          fetchPosUsers(bcLink, storePrettyUrl);
        },
        Nothing: () => {
          return;
        },
      });
    }
  }

  public componentWillUnmount() {
    const { cancelAutoSaveDraft } = this.props;
    cancelAutoSaveDraft();
  }

  private onClickPublish = () => {
    const { publishSchedule } = this.props;
    trackEvent("publish schedule");
    publishSchedule();
  };

  private onClickNotify = (notifyType: NotifyType, selectedUUIds: string[]) => {
    const { permalink, orderedSchedule, notifyPosUsers, startDate, endDate } =
      this.props;
    const requiredParams = [permalink, startDate, endDate];
    trackEvent("send schedule notification");
    Maybe.all(...requiredParams).match({
      Just: ([location, start, end]) => {
        const recipients: IPeople = orderedSchedule.people.filter((person) =>
          person.assignee.match({
            Just: (posUser) => selectedUUIds.includes(posUser.uuid),
            Nothing: () => false,
          })
        );
        notifyPosUsers(
          location as string,
          start as Date,
          end as Date,
          recipients,
          notifyType
        );
      },
      Nothing: () => null,
    });
  };

  public render() {
    const {
      clearFilters,
      dayView,
      endDate,
      startDate,
      filters,
      interactiveView,
      locationsRequestStatus,
      orderedSchedule,
      permalink,
      publicURL,
      publishedSchedule,
      rawSchedule,
      requestStatuses,
      showNotifyScheduleModal,
      toggleNotifyModal,
    } = this.props;

    const { posUsersGet, publishSchedulePost, draftScheduleGet, unavailGet } =
      requestStatuses;
    // These reasons are purely to put something short and sweet on the page
    // so that if the sales team/pre-release user reports it, we have
    // direction to investigate before diving into logs.
    const statuses = {
      "ERR-LOC": locationsRequestStatus,
      "ERR-POS": posUsersGet,
      "ERR-SCH": draftScheduleGet,
      "ERR-UNAVAIL": unavailGet,
    };

    const errorReasons = reduce(
      (errors, [code, status]: [string, RequestStatus]) => {
        if (status === RequestStatus.FAILURE) {
          errors.push(code);
        }
        return errors;
      },
      [] as string[]
    )(toPairs(statuses));

    if (errorReasons.length) {
      return (
        <StyledLoaderOverlay>
          We're sorry, an error occurred. Please try again. [
          {`${errorReasons.join(", ")}`}]
        </StyledLoaderOverlay>
      );
    } else if (
      any(
        (status) =>
          status === RequestStatus.REQUESTING ||
          status === RequestStatus.NOT_STARTED
      )(values(statuses))
    ) {
      return (
        <StyledLoaderOverlay>
          <div>Loading...</div>
        </StyledLoaderOverlay>
      );
    }

    const content = Maybe.all(startDate, endDate).match({
      Just: ([start, end]) => {
        return (
          <>
            <ScheduleTable
              key="schedule_table"
              dayView={dayView}
              schedule={orderedSchedule}
              startDate={start}
              endDate={end}
              interactiveView={interactiveView}
              clearFilters={clearFilters}
            />
            <RenderForFlag flag="workforceLaborDrawer">
              <LaborDrawer
                isDayView={dayView.isJust()}
                startDate={startDate}
                endDate={endDate}
              />
            </RenderForFlag>
          </>
        );
      },
      Nothing: () => {
        return (
          <div>
            Invalid date range, please check your selection and try again.
          </div>
        );
      },
    });
    const notifyModal = showNotifyScheduleModal === MODAL_STATUS.SHOW && (
      <NotifyModal
        onCancel={() => toggleNotifyModal(MODAL_STATUS.HIDE)}
        onSend={this.onClickNotify}
        people={orderedSchedule.people}
        publishSchedulePostRequestStatus={publishSchedulePost}
        publishedLinkAddress={publicURL}
      />
    );

    const publishedAt: Maybe<Date> = publishedSchedule.andThen(
      (published) => published.updatedAt
    );

    return (
      <PermalinkContext.Provider value={permalink.unwrapOr("")}>
        <ActionBar
          dayView={dayView}
          startDate={startDate}
          endDate={endDate}
          hasFiltersApplied={filters.roles.length > 0}
          publicURL={publicURL}
          publishedAt={publishedAt}
          rawSchedule={rawSchedule}
          onClickPublish={this.onClickPublish}
        />
        {content}
        {notifyModal}
      </PermalinkContext.Provider>
    );
  }
}

export default SchedulingPage;
